import React, { useState, useEffect, useMemo } from 'react';

import { Button } from '@eika/button';

import type { PaymentInterval } from '../../../../generated-types';
import useTranslation from '../../../../hooks/useTranslation';
import { arrayOfAll } from '../../../../utils/mapper/arrayOfAll';
import { EikaModal } from '../../../EikaModal';
import { SelectPanelList } from '../../../SelectPanelList';

import './PaymentIntervalModal.scss';

type PaymentIntervalSheetProps = {
    open?: boolean;
    setOpen: React.Dispatch<boolean>;
    onChange?: (interval: PaymentInterval) => void;
    paymentInterval?: PaymentInterval;
};

const paymentIntervalValues = arrayOfAll<PaymentInterval>()([
    'DAILY',
    'WEEKLY',
    'EVERY_TWO_WEEKS',
    'MONTHLY',
    'EVERY_TWO_MONTHS',
    'QUARTERLY',
    'EVERY_FOUR_MONTHS',
    'SEMIYEARLY',
    'YEARLY',
    'UNKNOWN',
]);

const PaymentIntervalModal: React.FC<PaymentIntervalSheetProps> = ({
    open = false,
    setOpen,
    onChange,
    paymentInterval,
}) => {
    const translated = useTranslation();
    const [interval, setInterval] = useState<PaymentInterval | undefined>(paymentInterval);
    const paymentIntervals = useMemo(() => paymentIntervalValues.filter((key) => key !== 'UNKNOWN'), []);

    const handleIntervalSelect = (interval: PaymentInterval) => {
        setInterval(interval);
    };

    const saveAndClose = () => {
        if (interval) {
            onChange?.(interval);
        }
        setOpen(false);
    };

    useEffect(() => {
        if (open) {
            setInterval(paymentInterval);
        }
    }, [open, paymentInterval, setInterval]);

    return (
        <EikaModal
            id="payment-interval-modal"
            open={open}
            setOpen={setOpen}
            footerContent={
                interval && paymentInterval && interval !== paymentInterval ? (
                    <Button onClick={saveAndClose}>{translated.save}</Button>
                ) : undefined
            }
            title={translated.dateField.paymentInterval.alias}
        >
            <div className="payment-interval-modal__grid-wrapper">
                <SelectPanelList id="payment-interval-selector">
                    {paymentIntervals.map((i) => (
                        <SelectPanelList.Item<PaymentInterval>
                            key={i}
                            value={i}
                            check={i === interval}
                            onClick={(value: PaymentInterval) => handleIntervalSelect(value)}
                        >
                            <p className="select-panel-item__payment-interval">
                                {translated.dateField.recurringPaymentInterval[i]}
                            </p>
                        </SelectPanelList.Item>
                    ))}
                </SelectPanelList>
            </div>
        </EikaModal>
    );
};

export default PaymentIntervalModal;
